/**
 * MyOffer - a student saying what they can do and when they are usually free
 * ---------------------------------------------------------------------------
 * The mirror of "post a chore". Instead of waiting for the right advert to
 * appear, a student can put themselves forward and let people come to them.
 *
 * Three parts: what you can help with, where you can get to, and an ordinary
 * weekly timetable - "Tuesdays 17:00 to 20:00" - which stays as you set it
 * until you change it. New students start with the usual shape of a school
 * week: after school on weekdays, most of the day at weekends.
 *
 * The timetable has two modes. Once saved it collapses to a plain read-only
 * summary, because that is what you want nine times out of ten; Edit puts the
 * controls back. Leaving a wall of inputs on screen made a settled timetable
 * look unfinished, and invited fiddling with something already correct.
 *
 * IMPORTANT, AND THE PAGE SAYS SO OUT LOUD
 * Publishing this does not agree to anything. Somebody who wants one of these
 * times has to ask, and the student decides then, knowing the job and the pay.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Calendar, Check, Trash2, Pause, Play, AlertTriangle, Plus, X, Pencil,
} from 'lucide-react';

import { offerApi, choreApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { dateTime, money } from '../utils/format';
import { HALF_HOURS } from '../utils/schedule';
import Loader from '../components/Loader';
import Modal from '../components/Modal';
import Badge from '../components/Badge';
import BookingRequests from '../components/BookingRequests';
import GuardianConsentBanner from '../components/GuardianConsentBanner';
import ConfirmEmailBanner from '../components/ConfirmEmailBanner';

import { useI18n } from '../hooks/useI18n';

// Monday first: it is a school week, not a calendar month.
const DAYS = [
  { day: 1, label: 'Monday', short: 'Mon' },
  { day: 2, label: 'Tuesday', short: 'Tue' },
  { day: 3, label: 'Wednesday', short: 'Wed' },
  { day: 4, label: 'Thursday', short: 'Thu' },
  { day: 5, label: 'Friday', short: 'Fri' },
  { day: 6, label: 'Saturday', short: 'Sat' },
  { day: 0, label: 'Sunday', short: 'Sun' },
];

const MIN_MINUTES = 30;
const MAX_HOURS = 14;

/**
 * The half-hour list this screen used to build for itself now lives in
 * utils/schedule.js as HALF_HOURS, because the posting form needs exactly the
 * same thing for exactly the same reason - see the note beside it. The reason
 * has not changed: <input type="time"> is drawn by the browser in the
 * machine's locale, which on an English setting means "5:00 PM", and this is
 * an Italian school where the clock has 24 hours on it.
 */
const TIMES = HALF_HOURS;

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
};

/** Checks a new window against the ones already on that day. */
/**
 * t is a PARAMETER here, and has to be.
 *
 * This function sits outside the component, so it cannot reach the t that
 * useI18n gives out. It used to call it anyway: adding a time window
 * without picking both times threw "t is not defined" and took the whole
 * page down - on the one screen a student uses to say when they are free.
 *
 * check-wiring cannot see this. It asks whether a FILE that calls t got it
 * from useI18n, and this file does, a few lines below. Scope was the thing
 * that was wrong, not the import.
 */
function windowProblem(from, to, existing, t) {
  if (!from || !to) return t('myOffer.fillBoth');
  const start = toMinutes(from);
  const end = toMinutes(to);

  if (end <= start) return t('myOffer.finishAfterStart');
  if (end - start < MIN_MINUTES) return t('myOffer.underMinutes', { count: MIN_MINUTES });
  if (end - start > MAX_HOURS * 60) return t('myOffer.longerThanHours', { count: MAX_HOURS });

  const clash = existing.find((w) => start < toMinutes(w.to) && end > toMinutes(w.from));
  if (clash) return t('myOffer.overlaps', { from: clash.from, to: clash.to });

  return null;
}

/** The weekly array, grouped by day for the screen. */
function byDay(weekly) {
  const out = {};
  DAYS.forEach(({ day }) => {
    out[day] = (weekly || [])
      .filter((w) => w.day === day)
      .map((w) => ({ from: w.from, to: w.to }))
      .sort((a, b) => toMinutes(a.from) - toMinutes(b.from));
  });
  return out;
}

export default function MyOffer() {
  const { t } = useI18n();
  const { user } = useAuth();

  // Up to three now - see models/Offer.js. `maxOffers` comes from the server
  // rather than being written here as well, so the number lives in one place.
  const [offers, setOffers] = useState([]);
  const [maxOffers, setMaxOffers] = useState(3);
  // Both numbers come from the server so there is only one copy of each - see
  // the note beside them in routes/offers.js. The fallback is only what to
  // show in the half-second before the first response lands.
  const [minDescription, setMinDescription] = useState(20);
  // The offer being created or changed, and whether its form is open.
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [defaultWeek, setDefaultWeek] = useState([]);
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Kept apart from `error` above, which belongs to the PAGE. This one belongs
  // to the offer form, which lives in a modal - and a modal covers the page,
  // so a failure reported to the page banner is reported to nobody. See the
  // note in components/Modal.jsx.
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  // The offer somebody has asked to take down, or null.
  const [confirmRemove, setConfirmRemove] = useState(null);

  // The form
  const [chosen, setChosen] = useState([]);
  const [description, setDescription] = useState('');
  const [city, setCity] = useState('');
  const [maxTravelKm, setMaxTravelKm] = useState('');
  const [week, setWeek] = useState({});
  const [draft, setDraft] = useState({});
  const [slotError, setSlotError] = useState({});

  // Read-only once saved; Edit puts the controls back.
  const [editingWeek, setEditingWeek] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mine, reqs, cats] = await Promise.all([
        offerApi.mine(),
        offerApi.requests(),
        choreApi.categories(),
      ]);
      setCategories(cats.categories);
      setRequests(reqs.requests || []);
      setDefaultWeek(mine.defaultWeek || []);
      setMaxOffers(mine.maxOffers || 3);
      setMinDescription(mine.minDescription || 20);
      setOffers(mine.offers || []);

      /**
       * ONE TIMETABLE, WHATEVER THEY HAVE PUBLISHED.
       *
       * It comes from the student now, not from an offer, so it is here
       * whether they have three offers or none - which means the editor
       * always has something to show and there is no "publish something
       * first" step in the way of setting your week.
       */
      setWeek(byDay(mine.availability || mine.defaultWeek || []));
      setEditingWeek((mine.availability || []).length === 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Opens the form, either empty or filled in from an existing offer.
   *
   * `editing` is the offer being changed, or null for a new one. Everything
   * the form needs is copied into its own state so that cancelling leaves the
   * list untouched.
   */
  const openForm = (existing = null) => {
    setEditing(existing);
    setChosen(existing?.categories || []);
    setDescription(existing?.description || '');
    setCity(existing?.area?.city || user.location?.city || '');
    setMaxTravelKm(existing?.area?.maxTravelKm ?? user.maxTravelKm ?? '');
    setFormOpen(true);
    setFormError('');
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setFormError('');
  };

  const setDraftField = (day, field, value) => {
    setDraft((prev) => ({ ...prev, [day]: { ...(prev[day] || {}), [field]: value } }));
    setSlotError((prev) => ({ ...prev, [day]: '' }));
  };

  const addWindow = (day) => {
    const { from, to } = draft[day] || {};
    const existing = week[day] || [];
    const problem = windowProblem(from, to, existing, t);
    if (problem) {
      setSlotError((prev) => ({ ...prev, [day]: problem }));
      return;
    }
    setWeek((prev) => ({
      ...prev,
      [day]: [...existing, { from, to }].sort((a, b) => toMinutes(a.from) - toMinutes(b.from)),
    }));
    setDraft((prev) => ({ ...prev, [day]: { from: '', to: '' } }));
    setSlotError((prev) => ({ ...prev, [day]: '' }));
  };

  const removeWindow = (day, index) => {
    setWeek((prev) => ({ ...prev, [day]: (prev[day] || []).filter((_, i) => i !== index) }));
  };

  const toggleCategory = (name) => {
    setChosen((prev) => (prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]));
  };

  /**
   * SAVING THE OFFER AND SAVING THE WEEK ARE TWO DIFFERENT ACTS.
   *
   * They used to be one call: publishing an offer sent the timetable with it.
   * That stopped making sense the moment the timetable became shared - saving
   * "dog walking" would have rewritten the week that "babysitting" also uses,
   * and editing your Tuesdays would have meant opening an advert.
   */
  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setSaving(true);

    const payload = {
      categories: chosen,
      description,
      city,
      maxTravelKm: maxTravelKm === '' ? null : Number(maxTravelKm),
    };

    try {
      if (editing) await offerApi.update(editing._id, payload);
      else await offerApi.publish(payload);
      setNotice(t('myOffer.saved'));
      closeForm();
      await load();
    } catch (err) {
      // Into the modal, which is what is on screen. See Modal.jsx.
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  /** The one timetable, shared by every offer. */
  const handleSaveWeek = async () => {
    setError('');
    setNotice('');
    setSaving(true);

    const weekly = [];
    DAYS.forEach(({ day }) => {
      (week[day] || []).forEach(({ from, to }) => weekly.push({ day, from, to }));
    });

    try {
      await offerApi.saveAvailability(weekly);
      setEditingWeek(false);
      setNotice(t('myOffer.weekSaved'));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const respond = async (id, accept) => {
    setError('');
    try {
      if (accept) {
        await offerApi.accept(id);
        setNotice(t('myOffer.accepted'));
      } else {
        await offerApi.decline(id);
        setNotice(t('myOffer.declined'));
      }
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  /**
   * Pausing is per offer. Somebody still happy to babysit but who has stopped
   * walking dogs can say exactly that, which is most of the reason for having
   * three.
   */
  const togglePaused = async (offer) => {
    setError('');
    try {
      await offerApi.setStatus(offer._id, offer.status === 'active' ? 'paused' : 'active');
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemove = async () => {
    const target = confirmRemove;
    setConfirmRemove(null);
    if (!target) return;
    try {
      await offerApi.remove(target._id);
      setNotice(t('myOffer.takenDown'));
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <Loader label={t('myOffer.loading')} />;

  const pending = requests.filter((r) => r.status === 'pending');
  const totalWindows = DAYS.reduce((n, { day }) => n + (week[day] || []).length, 0);

  return (
    <div className="container section">
      <div className="page-header">
        <h1>{t('myOffer.title')}</h1>
        <p>{t('myOffer.subtitle')}</p>
      </div>

      <ConfirmEmailBanner />
      <GuardianConsentBanner />

      {notice && <div className="alert alert-success" style={{ marginBottom: 'var(--space-md)' }}>{notice}</div>}
      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-md)' }}>{error}</div>}

      {/* ---------------- REQUESTS WAITING ON YOU ---------------- */}
      {/* The same list appears on "My work", which is where the notification
          about a request actually sends somebody - see
          components/BookingRequests.jsx. */}
      <BookingRequests requests={requests} onRespond={respond} />

      {/* ---------------- THE OFFERS ---------------- */}
      <div className="row-between" style={{ alignItems: 'baseline', marginTop: 'var(--space-lg)' }}>
        <h3>{t('myOffer.yourOffers', { count: offers.length, max: maxOffers })}</h3>
        {offers.length < maxOffers && (
          <button type="button" className="btn btn-primary btn-sm btn-inline" onClick={() => openForm()}>
            <Plus size={15} /> {t('myOffer.addAnother')}
          </button>
        )}
      </div>

      <div className="alert" style={{ marginBottom: 'var(--space-md)' }}>
        <AlertTriangle size={18} aria-hidden="true" />
        <div className="text-sm">{t('myOffer.notAnAgreement')}</div>
      </div>

      {offers.length === 0 ? (
        <div className="card">
          <p className="text-muted" style={{ margin: 0 }}>{t('myOffer.noneYet')}</p>
          <button
            type="button"
            className="btn btn-primary btn-inline"
            style={{ marginTop: 'var(--space-sm)' }}
            onClick={() => openForm()}
          >
            <Plus size={16} /> {t('myOffer.create')}
          </button>
        </div>
      ) : (
        <div className="stack" style={{ gap: 'var(--space-sm)' }}>
          {offers.map((o) => (
            <div className="card" key={o._id}>
              <div className="row-between" style={{ alignItems: 'flex-start', gap: 'var(--space-xs)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                    {(o.categories || []).map((c) => (
                      <Badge key={c} tone="grey">{c}</Badge>
                    ))}
                  </div>
                  <p className="text-sm" style={{ margin: 0 }}>{o.description}</p>
                  <p className="hint" style={{ marginTop: 4 }}>
                    {o.area?.city}
                    {o.area?.maxTravelKm ? ` · ${t('myOffer.upToKm', { km: o.area.maxTravelKm })}` : ''}
                  </p>
                </div>

                {/* A takedown is not a pause and must not look like one - the
                    student cannot undo it, so the badge says which it is. */}
                <Badge tone={o.hiddenAt ? 'red' : o.status === 'active' ? 'green' : 'grey'}>
                  {o.hiddenAt
                    ? t('myOffer.takenDownBadge')
                    : o.status === 'active'
                      ? t('myOffer.live')
                      : t('myOffer.paused')}
                </Badge>
              </div>

              {o.hiddenAt && (
                <div className="alert alert-warning" style={{ marginTop: 'var(--space-sm)' }}>
                  <AlertTriangle size={18} aria-hidden="true" />
                  <div className="text-sm">
                    <strong>{t('myOffer.takenDownTitle')}</strong>{' '}
                    {o.hiddenReason}
                  </div>
                </div>
              )}

              <div className="row" style={{ gap: 'var(--space-xs)', marginTop: 'var(--space-sm)', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-outline btn-sm btn-inline" onClick={() => openForm(o)}>
                  <Pencil size={14} /> {t('common.edit')}
                </button>

                {/* Pausing a taken-down offer would do nothing a moderator has
                    not already done, and offering it reads as if it might. */}
                {!o.hiddenAt && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm btn-inline"
                    onClick={() => togglePaused(o)}
                  >
                    {o.status === 'active'
                      ? <><Pause size={14} /> {t('myOffer.pause')}</>
                      : <><Play size={14} /> {t('myOffer.resume')}</>}
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-inline"
                  onClick={() => setConfirmRemove(o)}
                >
                  <Trash2 size={14} /> {t('myOffer.takeItDown')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- THE WEEK, WHICH IS SHARED ----------------
          Its own card, BELOW the offers since 25 August 2026, at the owner's
          request. It sat above them until then on the reasoning that it
          belongs to the person rather than to any one advert - which is still
          true, and is not the same question as what somebody wants to see
          first. What a student comes to this page to do is check their
          adverts; the timetable is a thing they set once and rarely touch.

          It still governs every offer at once. That used to be said in a line
          of explanation under the heading, removed the same day for being one
          more paragraph on a page that had too many - the heading and the
          single list of days say it well enough. */}
      <div className="card" style={{ marginTop: 'var(--space-lg)' }}>
        <div className="row-between" style={{ alignItems: 'baseline' }}>
          <h3>
            <Calendar size={16} aria-hidden="true" /> {t('myOffer.whenUsuallyFree')}
          </h3>
          {!editingWeek && (
            <button
              type="button"
              className="btn btn-ghost btn-sm btn-inline"
              onClick={() => setEditingWeek(true)}
            >
              <Pencil size={14} /> {t('common.edit')}
            </button>
          )}
        </div>

        {editingWeek ? (
          <>
            <div className="offer-days">
              {DAYS.map(({ day, label, short }) => {
                const mine = week[day] || [];
                const d = draft[day] || {};
                return (
                  <div className="offer-day-row" key={day}>
                    <div className="offer-day-label">
                      <strong>{short}</strong>
                    </div>

                    <div className="offer-day-body">
                      {mine.length > 0 && (
                        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                          {mine.map((w, i) => (
                            <span className="offer-range" key={`${w.from}-${w.to}`}>
                              {w.from}&ndash;{w.to}
                              <button
                                type="button"
                                onClick={() => removeWindow(day, i)}
                                aria-label={t('myOffer.removeWindow', { from: w.from, to: w.to, day: label })}
                              >
                                <X size={13} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="offer-add">
                        <label className="sr-only" htmlFor={`from-${day}`}>
                          {t('myOffer.startTimeOn', { day: label })}
                        </label>
                        <select
                          id={`from-${day}`}
                          value={d.from || ''}
                          onChange={(e) => setDraftField(day, 'from', e.target.value)}
                        >
                          <option value="">{t('myOffer.from')}</option>
                          {TIMES.map((time) => <option key={time} value={time}>{time}</option>)}
                        </select>
                        <span className="text-muted">{t('myOffer.toWord')}</span>
                        <label className="sr-only" htmlFor={`to-${day}`}>
                          {t('myOffer.finishTimeOn', { day: label })}
                        </label>
                        <select
                          id={`to-${day}`}
                          value={d.to || ''}
                          onChange={(e) => setDraftField(day, 'to', e.target.value)}
                        >
                          <option value="">{t('myOffer.to')}</option>
                          {TIMES.map((time) => <option key={time} value={time}>{time}</option>)}
                        </select>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm btn-inline"
                          onClick={() => addWindow(day)}
                        >
                          <Plus size={15} /> {t('myOffer.add')}
                        </button>
                      </div>

                      {slotError[day] && <span className="field-error">{slotError[day]}</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="row" style={{ gap: 'var(--space-xs)', marginTop: 'var(--space-sm)', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-primary btn-inline"
                onClick={handleSaveWeek}
                disabled={saving || totalWindows === 0}
              >
                {saving ? t('common.saving') : <><Check size={16} /> {t('myOffer.saveMyWeek')}</>}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-inline"
                onClick={() => setWeek(byDay(defaultWeek))}
              >
                {t('myOffer.resetToUsual')}
              </button>
            </div>
          </>
        ) : (
          /* ---- Confirmed: the boxes go, the times stay ---- */
          <div className="offer-week-summary">
            <p className="offer-summary-title">{t('myOffer.availableOn')}</p>
            {DAYS.filter(({ day }) => (week[day] || []).length > 0).map(({ day, short }) => (
              <p className="offer-summary-line" key={day}>
                <span className="offer-summary-day">{short}:</span>
                <span className="offer-summary-times">
                  {(week[day] || []).map((w) => `${w.from}-${w.to}`).join(', ')}
                </span>
              </p>
            ))}
            {DAYS.every(({ day }) => (week[day] || []).length === 0) && (
              <p className="text-muted">{t('myOffer.nothingSet')}</p>
            )}
          </div>
        )}

      </div>

      {/* ---------------- ONE OFFER, BEING WRITTEN ---------------- */}
      <Modal
        open={formOpen}
        onClose={closeForm}
        title={editing ? t('myOffer.editOffer') : t('myOffer.create')}
        error={formError}
      >
        <form onSubmit={handleSave} className="form">
          {/* What you can do */}
          <div className="field">
            <span className="label">{t('myOffer.whatCanYouDo')}</span>
            <div className="row" style={{ gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
              {categories.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={`btn btn-sm btn-inline ${chosen.includes(name) ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => toggleCategory(name)}
                >
                  {name}
                </button>
              ))}
            </div>
            <p className="hint">{t('myOffer.pickFive')}</p>
          </div>

          <div className="field">
            <label className="label" htmlFor="offer-description">{t('myOffer.aboutYou')}</label>
            <textarea
              id="offer-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder={t('myOffer.aboutPlaceholder')}
            />
            {/* Say how much more is needed WHILE they type. Being told the
                minimum only after pressing Publish is how somebody writes a
                short line, gets refused, and has to guess by how much. */}
            <span className="hint">
              {description.length}/1000
              {description.length < minDescription && (
                <> &middot; {t('myOffer.atLeastChars', { count: minDescription })}</>
              )}
            </span>
          </div>

          <div className="form-row">
            <div className="field">
              <label className="label" htmlFor="offer-city">{t('myOffer.whichTown')}</label>
              <input id="offer-city" value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div className="field">
              <label className="label" htmlFor="offer-travel">{t('myOffer.howFar')}</label>
              <select
                id="offer-travel"
                value={maxTravelKm}
                onChange={(e) => setMaxTravelKm(e.target.value)}
              >
                <option value="">{t('myOffer.doNotMind')}</option>
                <option value="2">{t('myOffer.upToKm', { km: 2 })}</option>
                <option value="5">{t('myOffer.upToKm', { km: 5 })}</option>
                <option value="10">{t('myOffer.upToKm', { km: 10 })}</option>
                <option value="20">{t('myOffer.upToKm', { km: 20 })}</option>
                <option value="50">{t('myOffer.upToKm', { km: 50 })}</option>
              </select>
            </div>
          </div>

          <p className="hint">{t('myOffer.weekIsShared')}</p>

          <div className="row" style={{ gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
            <button type="submit" className="btn btn-primary btn-inline" disabled={saving}>
              {saving ? t('common.saving') : editing ? t('myOffer.saveChanges') : t('myOffer.publish')}
            </button>
            <button type="button" className="btn btn-ghost btn-inline" onClick={closeForm}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(confirmRemove)}
        onClose={() => setConfirmRemove(null)}
        title={t('myOffer.takeDownQ')}
        actions={
          <>
            <button type="button" className="btn btn-ghost btn-inline" onClick={() => setConfirmRemove(null)}>
              {t('myOffer.keepItUp')}
            </button>
            <button type="button" className="btn btn-danger btn-inline" onClick={handleRemove}>
              {t('myOffer.takeItDown')}
            </button>
          </>
        }
      >
        <p>{t('myOffer.takeDownBody')}</p>
      </Modal>
    </div>
  );
}
